import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useTour } from "./useTour";
import type { TourStep } from "./tourSteps";
import type { Rect } from "./TourOverlay";

const TOOLTIP_WIDTH = 360;
const TOOLTIP_GAP = 16;
const VIEWPORT_MARGIN = 16;
// Worst-case tooltip height; only used to keep it inside the viewport when
// it has to flip below the target. Real height comes from the actual
// rendered card after layout.
const APPROX_HEIGHT = 240;

function computePosition(rect: Rect): React.CSSProperties {
  if (typeof window === "undefined") return {};
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer right of target; flip to left, then below, if it would overflow.
  let left = rect.left + rect.width + TOOLTIP_GAP;
  let top = rect.top;
  if (left + TOOLTIP_WIDTH > vw - VIEWPORT_MARGIN) {
    left = rect.left - TOOLTIP_WIDTH - TOOLTIP_GAP;
  }
  if (left < VIEWPORT_MARGIN) {
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN),
    );
    top = rect.top + rect.height + TOOLTIP_GAP;
  }
  if (top + APPROX_HEIGHT > vh - VIEWPORT_MARGIN) {
    top = Math.max(VIEWPORT_MARGIN, vh - APPROX_HEIGHT - VIEWPORT_MARGIN);
  }
  if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
  return {
    position: "fixed",
    top,
    left,
    width: TOOLTIP_WIDTH,
  };
}

export function TourTooltip({
  step,
  rect,
}: {
  step: TourStep;
  rect: Rect | null;
}) {
  const tour = useTour();
  const navigate = useNavigate();
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const centered = !rect;
  const style = useMemo<React.CSSProperties>(() => {
    if (!rect) {
      if (typeof window === "undefined") return { width: TOOLTIP_WIDTH };
      return {
        width: Math.min(TOOLTIP_WIDTH + 40, window.innerWidth - 32),
      };
    }
    return computePosition(rect);
  }, [rect]);

  // Focus the first interactive element each time the step changes so
  // keyboard users land somewhere sensible.
  useEffect(() => {
    const t = tooltipRef.current;
    if (!t) return;
    const first = t.querySelector<HTMLElement>(
      "button, [tabindex]:not([tabindex='-1'])",
    );
    first?.focus();
  }, [step.id]);

  // Esc → skip. Tab cycling stays inside the tooltip (focus trap) so users
  // can't accidentally land on a sidebar item that's been visually dimmed.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        tour.skip();
        return;
      }
      if (e.key === "Tab") {
        const t = tooltipRef.current;
        if (!t) return;
        const focusables = Array.from(
          t.querySelectorAll<HTMLElement>(
            "button:not([disabled]), [tabindex]:not([tabindex='-1'])",
          ),
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [tour]);

  function handlePrimary() {
    if (step.cta) {
      navigate(step.cta.route);
      tour.complete();
      return;
    }
    tour.next();
  }

  const primaryLabel = step.cta
    ? step.cta.label
    : tour.isLast
      ? "Listo"
      : tour.isFirst
        ? "Empezar"
        : "Siguiente";

  const card = (
    <motion.div
      ref={tooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-step-${step.id}-title`}
      style={style}
      className={`${
        centered ? "" : "z-[1001] "
      }bg-surface border border-border rounded-2xl shadow-glass p-5`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Paso {tour.currentIndex + 1} de {tour.totalSteps}
          </p>
          <h3
            id={`tour-step-${step.id}-title`}
            className="text-lg font-black tracking-tight text-ink leading-tight"
          >
            {step.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => tour.skip()}
          aria-label="Saltar tour"
          className="p-1 -mr-1 -mt-1 text-ink-3 hover:text-ink transition-colors rounded-md cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm text-ink-2 mb-5 leading-relaxed">{step.body}</p>

      <div
        className="flex items-center gap-1 mb-4"
        aria-hidden="true"
      >
        {Array.from({ length: tour.totalSteps }).map((_, i) => (
          <span
            key={i}
            className="h-1 rounded-full transition-all duration-300 flex-1"
            style={{
              backgroundColor: i <= tour.currentIndex ? "#0F0F0F" : "#E8E4DF",
            }}
          />
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          {!tour.isFirst && (
            <button
              type="button"
              onClick={tour.prev}
              className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Atrás
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => tour.skip()}
            className="text-sm text-ink-3 hover:text-ink transition-colors cursor-pointer"
          >
            Saltar
          </button>
          <button
            type="button"
            onClick={handlePrimary}
            className="inline-flex items-center gap-2 bg-ink text-white text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 hover:scale-[1.02] cursor-pointer"
          >
            {primaryLabel}
            {!step.cta && <ArrowRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );

  if (centered) {
    return (
      <div className="fixed inset-0 z-[1001] flex items-center justify-center pointer-events-none">
        <div className="pointer-events-auto">{card}</div>
      </div>
    );
  }
  return card;
}
